<template>
  <div>
    <hr v-if="subType !== 'voucher_fuel'">

    <div v-if="subType !== 'voucher_fuel'" class="voucher-options">
      <section class="voucher-options-section mb-3">
        <h6 class="voucher-options-section__title">{{ $t('flexben.benefit.voucher_options.sport_section_title') }}</h6>
        <p class="voucher-options-section__hint">{{ $t('flexben.benefit.voucher_options.sport_section_hint') }}</p>

        <div class="row">
          <div class="col-12 col-lg-6 form-group">
            <div class="voucher-toggle-card">
              <div class="voucher-toggle-card__content">
                <div class="voucher-toggle-card__label-row">
                  <label for="threshold" class="voucher-toggle-card__label mb-0">
                    {{ $t('flexben.benefit.thresholds.gym_membership') }}
                  </label>
                  <span id="threshold_description" class="flexben-tooltip-icon">i</span>
                  <b-tooltip :target="'threshold_description'" variant="flexben" placement="top">
                    <div class="text-left">{{ $t('flexben.benefit.thresholds.tooltip_gym') }}</div>
                  </b-tooltip>
                </div>
                <small class="text-muted d-block">{{ $t('flexben.benefit.voucher_options.gym_membership_hint') }}</small>
              </div>
              <b-form-checkbox
                id="threshold"
                class="voucher-toggle-card__switch"
                @change="$emit('addThreshold', 'gym_membership', gymMembership)"
                v-model="gymMembership"
                :disabled="disabled"
                switch
              />
            </div>
          </div>

          <div class="col-12 col-lg-6 form-group">
            <div :class="['voucher-toggle-card', {'voucher-toggle-card--disabled': !gymMembership}]">
              <div class="voucher-toggle-card__content">
                <label for="recipient_option" class="voucher-toggle-card__label mb-0">
                  {{ $t('flexben.benefit.fields.recipient_option') }}
                </label>
                <small class="text-muted d-block">
                  {{ gymMembership
                    ? $t('flexben.benefit.voucher_options.recipient_option_hint')
                    : $t('flexben.benefit.voucher_options.recipient_option_requires_gym') }}
                </small>
              </div>
              <b-form-checkbox
                id="recipient_option"
                class="voucher-toggle-card__switch"
                v-model="fields.recipient_option"
                :disabled="disabled || !gymMembership"
                switch
              />
            </div>
          </div>
        </div>
      </section>

      <section v-if="!forCampaign" class="voucher-options-section mb-3">
        <h6 class="voucher-options-section__title">{{ $t('flexben.benefit.voucher_options.stock_section_title') }}</h6>
        <p class="voucher-options-section__hint">{{ $t('flexben.benefit.voucher_options.stock_section_hint') }}</p>

        <div class="row">
          <div class="col-12 col-lg-6 form-group">
            <div class="voucher-toggle-card">
              <div class="voucher-toggle-card__content">
                <label for="mark_as_new_import_voucher" class="voucher-toggle-card__label mb-0">
                  {{ $t('flexben.benefit.fields.mark_as_new_import_voucher') }}
                </label>
                <small class="text-muted d-block">{{ $t('flexben.benefit.voucher_options.mark_as_new_import_voucher_hint') }}</small>
              </div>
              <b-form-checkbox
                id="mark_as_new_import_voucher"
                class="voucher-toggle-card__switch"
                v-model="fields.mark_as_new_import_voucher"
                :disabled="disabled"
                switch
              />
            </div>
          </div>

          <div class="col-12 col-lg-6 form-group">
            <div class="voucher-toggle-card">
              <div class="voucher-toggle-card__content">
                <label for="partner_voucher_mail" class="voucher-toggle-card__label mb-0">
                  {{ $t('flexben.benefit.fields.partner_voucher_mail') }}
                </label>
                <small class="text-muted d-block">{{ $t('flexben.benefit.voucher_options.partner_voucher_mail_hint') }}</small>
              </div>
              <b-form-checkbox
                id="partner_voucher_mail"
                class="voucher-toggle-card__switch"
                v-model="fields.partner_voucher_mail"
                :disabled="disabled"
                switch
              />
            </div>
          </div>
        </div>
      </section>

      <section class="voucher-options-section mb-3">
        <h6 class="voucher-options-section__title">{{ $t('flexben.benefit.voucher_options.campaign_section_title') }}</h6>
        <p class="voucher-options-section__hint">{{ $t('flexben.benefit.voucher_options.campaign_section_hint') }}</p>

        <div class="row">
          <div class="col-12 col-lg-6 form-group">
            <div class="voucher-toggle-card">
              <div class="voucher-toggle-card__content">
                <div class="voucher-toggle-card__label-row">
                  <label for="for_campaign" class="voucher-toggle-card__label mb-0">
                    {{ $t('flexben.benefit.fields.for_campaign') }}
                  </label>
                  <span id="for_campaign_tool_tip" class="flexben-tooltip-icon">i</span>
                  <b-tooltip :target="'for_campaign_tool_tip'" variant="flexben" placement="top">
                    <div class="text-left">{{ $t('flexben.benefit.fields.for_campaign_info') }}</div>
                  </b-tooltip>
                </div>
                <small class="text-muted d-block">{{ $t('flexben.benefit.voucher_options.for_campaign_hint') }}</small>
              </div>
              <b-form-checkbox
                id="for_campaign"
                class="voucher-toggle-card__switch"
                v-model="forCampaign"
                switch
              />
            </div>
          </div>
        </div>

        <div v-if="forCampaign" class="row mt-2">
          <div class="col-12 text-danger mb-2">{{ $t('flexben.benefit.fields.for_campaign_info') }}</div>
          <div class="col-12 col-md-6 form-group">
            <label class="col-form-label form-fields-required" for="campaign_partner">
              {{ $t('flexben.benefit.fields.for_campaign_partner') }}
            </label>

            <multiselect
                id="campaign_partner"
                name="partner"
                v-model="selectedCampaignPartner"
                :options="campaignPartners"
                label="name"
                track-by="id"
                :multiple="false"
                :searchable="true"
                :close-on-select="true"
                :clear-on-select="false"
                :placeholder="$t('flexben.benefit.fields.for_campaign_partner_placeholder')"
                :show-labels="false"
            />
          </div>
          <div class="col-12 col-md-6 form-group">
            <label class="col-form-label form-fields-required" for="campaign_brand">
              {{ $t('flexben.benefit.fields.for_campaign_brand') }}
            </label>

            <multiselect
                id="campaign_brand"
                name="brand"
                v-model="selectedCampaignBrand"
                :options="campaignBrands"
                label="name"
                track-by="id"
                :multiple="false"
                :searchable="true"
                :close-on-select="true"
                :clear-on-select="false"
                :placeholder="$t('flexben.benefit.fields.for_campaign_brand_placeholder')"
                :show-labels="false"
            />
          </div>
        </div>
      </section>
    </div>

    <hr>
    <div class="row">
      <div class="col-12 col-md-6 form-group">
        <label for="delivery_type" class="form-fields-required col-form-label">
          {{ $t('flexben.benefit.fields.delivery_type') }}</label>
        <select class="form-control" id="delivery_type" v-model="fields.delivery_type"
                @change="clearVoucherValue()"
                :disabled="disabled"
                :class="{'border-danger':(Object.keys(errorsBag).includes('delivery_type'))}">
          <option :value="null">-</option>
          <option v-for="(type, index) in deliveryTypes"
                  :value="type"
                  :key="index">{{ $t('flexben.benefit.delivery_types.'+type) }}
          </option>
        </select>
      </div>

      <div class="col-12 col-md-6 form-group"
           v-show="fields.delivery_type!==null && fields.delivery_type!=='card'">
        <label for="voucher_value" class="form-fields-required col-form-label">
          {{ $t('flexben.benefit.fields.voucher_billing_value') }}</label>
        <input type="number" id="voucher_value" v-model="fields.billing_value" class="form-control"
               :disabled="disabled"
               :class="{'border-danger':(Object.keys(errorsBag).includes('voucher_billing_value'))}"/>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-md-6 form-group">
        <label for="discount_type" class="form-fields-required col-form-label">
          {{ $t('flexben.benefit.fields.discount.type') }}</label>
        <select class="form-control" id="discount_type" v-model="discountData.discount_type"
                :disabled="disabled"
                :class="{'border-danger':(Object.keys(errorsBag).includes('discount_types'))}">
          <option :value="null">-</option>
          <option v-for="(type, index) in discountTypes"
                  :value="type.value"
                  :key="index">{{ type.name }}
          </option>
        </select>
      </div>
      <div class="col-12 col-md-6 form-group"
           v-show="discountData.discount_type">
        <label for="discount_value" class="form-fields-required col-form-label">
          {{ $t('flexben.benefit.fields.discount.value') }}</label>
        <label for="discount_value" class="form-fields-required col-form-label text-danger">
          {{ $t('flexben.discount.info.total_value') }}</label>
        <input type="number" step="0.1" id="voucher_value" v-model="discountData.discount_value" class="form-control"
               :disabled="disabled"
               :class="{'border-danger':(Object.keys(errorsBag).includes('discount_value'))}"/>
      </div>
      <div class="col-12 col-md-6 form-group"
           v-show="discountData.discount_type">
        <ul>
          <li>
            Discount: {{ discountData.discount_value }}
            {{ (discountData.discount_type === 1) ? '%' : 'RON' }}
          </li>

          <li v-if="discountData.discount_type === 1">
            Discounted Price: {{ fields.billing_value * (1 - discountData.discount_value / 100) }} RON
          </li>
          <li v-else>
            Discounted Price: {{ fields.billing_value - discountData.discount_value }} RON
          </li>
        </ul>
      </div>
    </div>
  </div>

</template>

<script>
import {BFormCheckbox, BTooltip} from "bootstrap-vue";
import Multiselect from "vue-multiselect";

export default {
  name: "BenefitVoucherFormFields",
  components: {
    'b-tooltip': BTooltip,
    "b-form-checkbox": BFormCheckbox,
    Multiselect

  },
  props: {
    for_campaign: {
      type: Boolean,
      default: false,
    },
    subType: {
      type: String,
      required: true
    },
    fields: {
      type: Object,
      default: function () {
        return {};
      },
      required: true
    },
    options: {
      type: Object,
      default: function () {
        return {};
      },
      required: true
    },
    partner_id: {
      default: null,
    },
    brand_availability: {
      default: null,
    },
    deliveryTypes: {
      type: Array,
      required: true,
      default: function () {
        return []
      },
    },
    discountTypes: {
      type: Array,
      required: true,
      default: function () {
        return []
      },
    },
    discountData: {
      type: Object,
      required: true
    },
    errorsBag: {
      type: Object,
      default: function () {
        return {};
      },
      required: true
    },
    voucherValueUrl: {
      default: null,
    },
    formAction: {
      required: true,
      type: String
    },
    benefitStatus: {
      required: true,
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
  },
  data: function () {
    return {
      gymMembership: false,


    };
  },
  computed: {
    forCampaign: {
      get() {
        return this.for_campaign;
      },
      set(value) {
        if (value) {
          this.fields.mark_as_new_import_voucher = false;
          this.fields.partner_voucher_mail = false;
          this.fields.recipient_option = false;
        }
        this.$emit('update:for_campaign', value);
      }
    },
    campaignPartners() {
      return this.options.for_campaign && this.options.for_campaign.partners
        ? this.options.for_campaign.partners
        : [];
    },
    campaignBrands() {
      return this.options.for_campaign && this.options.for_campaign.brands
        ? this.options.for_campaign.brands
        : [];
    },
    selectedCampaignPartner: {
      get() {
        if (this.partner_id === null || this.partner_id === undefined || !this.campaignPartners.length) {
          return null;
        }

        return this.campaignPartners.find(partner => {
          return Number(partner.id) === Number(this.partner_id);
        }) || null;
      },
      set(value) {
        this.$emit('update:partner_id', value ? value.id : null);
      }
    },
    selectedCampaignBrand: {
      get() {
        if (this.brand_availability === null || this.brand_availability === undefined || !this.campaignBrands.length) {
          return null;
        }

        return this.campaignBrands.find(brand => {
          return Number(brand.id) === Number(this.brand_availability);
        }) || null;
      },
      set(value) {
        this.$emit('update:brand_availability', value ? value.id : null);
      }
    }
  },


  watch: {
    gymMembership(value) {
      if (!value) {
        this.fields.recipient_option = false;
      }
    }
  },
  created() {
    this.gymMembership = this.getGymMembership();
  },
  methods: {
    creatDiscountData() {
      // this.discountData.discount_type = this.fields.discount_type;

    },
    clearVoucherValue() {
      if (this.disabled) {
        return;
      }

      this.fields.billing_value = 0;
    },
    getGymMembership() {
      if (!this.fields.thresholds.length) {
        return false;
      }

      return this.fields.thresholds.find(function (item) {return item.name === 'gym_membership'}) ?
        this.fields.thresholds.find(function (item) {return item.name === 'gym_membership'})['value'] : false
    }
  }
}
</script>
<style scoped>
.voucher-options-section {
  padding: 1rem 1.25rem;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  background-color: #fafbfc;
}

.voucher-options-section__title {
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.25rem;
}

.voucher-options-section__hint {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.75rem;
}

.voucher-toggle-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  height: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #fff;
}

.voucher-toggle-card--disabled {
  opacity: 0.72;
  background-color: #f8f9fa;
}

.voucher-toggle-card__content {
  flex: 1;
  min-width: 0;
}

.voucher-toggle-card__label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.voucher-toggle-card__label {
  font-weight: 600;
  color: #212529;
}

.voucher-toggle-card__switch {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.flexben-tooltip-icon {
  font-family: "Times New Roman", sans-serif;
  color: #dc3545;
  border: 1px solid gray;
  background-color: #fff;
  font-size: 16px;
  font-weight: bold;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  text-align: center;
  cursor: pointer;
  line-height: initial;
}
</style>
